import {
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  Alert,
  Chip,
  Tooltip,
} from "@mui/material";
import { memo } from "react";
import { XIcon, HelpCircleIcon, BookmarkIcon } from "../Icons";
import { AdditionalInfo } from "../../graphql/generated";
import colors from "../../styles/colors";

import styles from "./dialog-components.module.scss";
import { isFunction } from "lodash";

interface Props {
  description?: string;
  title?: string;
  additionalInfo?: AdditionalInfo | null;
  deprecated?: boolean;
  resourceDocLink?: string;
  onClose: () => void;
  showLibraryBookmark?: boolean;
  resourceInLibrary?: boolean;
  setAddToLibraryDialogOpen?: (b: boolean) => void;
  readOnly?: boolean;
}

const TitleSectionComponent: React.FC<Props> = ({
  description,
  title,
  additionalInfo,
  deprecated,
  resourceDocLink,
  onClose,
  showLibraryBookmark,
  resourceInLibrary,
  setAddToLibraryDialogOpen,
  readOnly,
}) => {
  function handleBookmarkClick() {
    if (resourceInLibrary) {
      return;
    } else {
      isFunction(setAddToLibraryDialogOpen)
        ? setAddToLibraryDialogOpen(true)
        : console.error("No function provided for setAddToLibraryDialogOpen");
    }
  }

  return (
    <DialogTitle
      classes={{
        root: styles.title,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography fontSize={28} fontWeight={600}>
              {title}
            </Typography>
            {resourceDocLink && (
              <IconButton
                href={resourceDocLink}
                target="_blank"
                rel="noreferrer"
                color="primary"
              >
                <HelpCircleIcon />
              </IconButton>
            )}
            {deprecated && (
              <Chip
                color="warning"
                label="Deprecated"
                size="small"
                variant="filled"
              />
            )}
          </Stack>
          <Typography fontSize={18}>{description}</Typography>
        </Stack>

        <Stack direction={"row"} spacing={3}>
          {showLibraryBookmark && (
            <Tooltip
              title={
                resourceInLibrary
                  ? "Resource is in Library"
                  : "Add resource to Library"
              }
            >
              {/* span around button allows Tooltip to show when button is disabled, otherwise there are console errors */}
              <span>
                <IconButton
                  color={resourceInLibrary ? "primary" : undefined}
                  onClick={handleBookmarkClick}
                  disabled={readOnly}
                  data-testid="source-configuration-bookmark-icon"
                >
                  <BookmarkIcon
                    fill={resourceInLibrary ? colors.pixelPointBlue : "none"}
                  />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <IconButton className={styles.close} onClick={onClose}>
            <XIcon strokeWidth={"3"} width={"28"} />
          </IconButton>
        </Stack>
      </Stack>
      {additionalInfo && (
        <Alert
          severity="info"
          className={styles["info"]}
          data-testid="info-alert"
        >
          <Typography>
            {additionalInfo.message}
            {additionalInfo.documentation?.map((d) => (
              <a href={d.url} rel="noreferrer" target="_blank" key={d.url}>
                <br></br>
                {d.text}
              </a>
            ))}
          </Typography>
        </Alert>
      )}
    </DialogTitle>
  );
};

export const TitleSection = memo(TitleSectionComponent);
